import { useMemo, useState } from 'react';
import { MetricChart } from './charts';
import { SelectField } from './components/ui';

/**
 * RangeOfMotionChart — max / min joint angle per session for one joint, with the latest range
 * (max − min) as the headline and its change since the first session.
 *
 * data: [{ name | _name, angleMin, angleMax, timestamp | startTime, game? }] — one entry per joint
 * per session (built from session.results.jointsData). emptyText is optional.
 */
const toMs = (x) => {
    if (x == null || x === '') return null;
    if (typeof x === 'number') return x;
    const n = Number(x);
    if (Number.isFinite(n)) return n;
    const p = Date.parse(x);
    return Number.isFinite(p) ? p : null;
};
const num = (x) => (x == null || x === '' ? NaN : Number(x));
/** 'RightElbowAngle' → 'Right elbow' */
function prettyJoint(name) {
    const s = String(name || '').replace(/Angle$/, '').replace(/[_-]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').trim();
    return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : 'Joint';
}
const dayKey = (ms) => new Date(ms).toDateString();
const fmtDay = (ms) => {
    const d = new Date(ms);
    return d.toLocaleDateString([], d.getFullYear() === new Date().getFullYear() ? { day: 'numeric', month: 'short' } : { day: 'numeric', month: 'short', year: 'numeric' });
};
const fmtDeg = (x) => (Number.isFinite(x) ? `${Math.round(x)}°` : '—');

const S = {
    wrap: { display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 },
    top: { display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', gap: '12px 32px' },
    stat: { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 },
    big: { display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: '4px 12px' },
    value: { fontWeight: 700, fontSize: 32, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' },
    caption: { fontWeight: 600, fontSize: 14, letterSpacing: 1.7, textTransform: 'uppercase', color: 'var(--egdl-text-secondary)' },
    empty: { minHeight: 200, display: 'flex', alignItems: 'center', padding: 24, background: 'var(--egdl-surface-sunken)', fontSize: 17, lineHeight: 1.5, color: 'var(--egdl-text-secondary)' },
};

function RangeOfMotionChart({ data, emptyText = 'No range-of-motion data yet' }) {
    const rows = useMemo(() => (Array.isArray(data) ? data : [])
        .map((d) => ({ joint: d?.name || d?._name, t: toMs(d?.timestamp ?? d?.startTime), min: num(d?.angleMin), max: num(d?.angleMax) }))
        .filter((d) => d.joint && d.t && (Number.isFinite(d.min) || Number.isFinite(d.max))), [data]);

    const joints = useMemo(() => [...new Set(rows.map((r) => r.joint))], [rows]);
    const [picked, setPicked] = useState('');
    const joint = joints.includes(picked) ? picked : joints[0] || '';

    // One point per session (same start time), oldest first.
    const pts = useMemo(() => {
        const by = new Map();
        rows.forEach((r) => {
            if (r.joint !== joint) return;
            const cur = by.get(r.t);
            if (!cur) by.set(r.t, { ...r });
            else {
                if (Number.isFinite(r.min)) cur.min = Number.isFinite(cur.min) ? Math.min(cur.min, r.min) : r.min;
                if (Number.isFinite(r.max)) cur.max = Number.isFinite(cur.max) ? Math.max(cur.max, r.max) : r.max;
            }
        });
        return [...by.values()].sort((a, b) => a.t - b.t);
    }, [rows, joint]);

    if (!rows.length) return <div style={S.empty}>{emptyText}</div>;

    const perDay = {};
    pts.forEach((p) => { perDay[dayKey(p.t)] = (perDay[dayKey(p.t)] || 0) + 1; });
    const xLabels = pts.map((p) => (perDay[dayKey(p.t)] > 1
        ? `${fmtDay(p.t)} ${new Date(p.t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
        : fmtDay(p.t)));

    const range = (p) => (Number.isFinite(p.max) && Number.isFinite(p.min) ? p.max - p.min : NaN);
    const last = pts[pts.length - 1];
    const first = pts[0];
    const delta = pts.length > 1 ? range(last) - range(first) : NaN;

    return (
        <div style={S.wrap}>
            <div style={S.top}>
                {joints.length > 1 ? (
                    <SelectField
                        label="Joint"
                        value={joint}
                        onChange={setPicked}
                        options={joints.map((j) => ({ value: j, label: prettyJoint(j) }))}
                        style={{ flex: '0 1 260px', minWidth: 180 }}
                    />
                ) : (
                    <div style={S.stat}>
                        <span style={S.caption}>Joint</span>
                        <span style={{ fontWeight: 600, fontSize: 17 }}>{prettyJoint(joint)}</span>
                    </div>
                )}
                <div style={S.stat}>
                    <div style={S.big}>
                        <span style={S.value}>{fmtDeg(range(last))}</span>
                        {Number.isFinite(delta) && Math.round(delta) !== 0 ? (
                            <span style={{ fontSize: 17, color: delta > 0 ? 'var(--egdl-success-ink)' : 'var(--egdl-text-secondary)' }}>
                                {delta > 0 ? '▲' : '▼'} {Math.abs(Math.round(delta))}° since {fmtDay(first.t)}
                            </span>
                        ) : Number.isFinite(delta) ? (
                            <span style={{ fontSize: 17, color: 'var(--egdl-text-secondary)' }}>No change since {fmtDay(first.t)}</span>
                        ) : null}
                    </div>
                    <span style={S.caption}>Range, latest session · {fmtDeg(last.min)} to {fmtDeg(last.max)}</span>
                </div>
            </div>
            <MetricChart
                series={[
                    { label: 'Max angle', unit: '°', dp: 0, idx: 0, data: pts.map((p) => (Number.isFinite(p.max) ? p.max : null)) },
                    { label: 'Min angle', unit: '°', dp: 0, idx: 1, data: pts.map((p) => (Number.isFinite(p.min) ? p.min : null)) },
                ]}
                xLabels={xLabels}
                duration={Math.max(1, pts.length - 1)}
                height={260}
                brush={pts.length > 24}
                leftUnit="°"
                emptyText="One session so far"
            />
        </div>
    );
}

export default RangeOfMotionChart;
